import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckSquare,
  DollarSign,
  Lightbulb,
  Loader2,
  PieChart,
  Scale,
  TrendingUp,
  UserCheck,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  COLOR_PALETTE,
  REPORTS,
  defaultFilters,
  type CommonReportFilters,
  type ReportConfig,
} from "../types";
import { ReportFilters } from "../components/ReportFilters";
import {
  exportReport,
  useActionsTrackerReport,
  useActivitySummaryReport,
  useBudgetVsActualReport,
  useDecisionsLogReport,
  useExpenseAnalysisReport,
  useIdeasPipelineReport,
  useMeetingAttendanceReport,
  useOverdueItemsReport,
  useProfitabilityReport,
  useProjectHealthReport,
  useProjectTimelineReport,
  useTasksStatusReport,
  useTeamWorkloadReport,
  type ReportExportColumn,
} from "../hooks/useReports";
import { ProjectHealthReport } from "../components/reports/ProjectHealthReport";
import { OverdueItemsReport } from "../components/reports/OverdueItemsReport";
import { ProjectTimelineReport } from "../components/reports/ProjectTimelineReport";
import { BudgetVsActualReport } from "../components/reports/BudgetVsActualReport";
import { ExpenseAnalysisReport } from "../components/reports/ExpenseAnalysisReport";
import { ProfitabilityReport } from "../components/reports/ProfitabilityReport";
import { TeamWorkloadReport } from "../components/reports/TeamWorkloadReport";
import { ActivitySummaryReport } from "../components/reports/ActivitySummaryReport";
import { MeetingAttendanceReport } from "../components/reports/MeetingAttendanceReport";
import { TasksStatusReport } from "../components/reports/TasksStatusReport";
import { ActionsTrackerReport } from "../components/reports/ActionsTrackerReport";
import { DecisionsLogReport } from "../components/reports/DecisionsLogReport";
import { IdeasPipelineReport } from "../components/reports/IdeasPipelineReport";
import { Button } from "@/components/ui/button";

const ICON_MAP: Record<string, LucideIcon> = {
  Activity,
  AlertCircle,
  Calendar,
  CheckSquare,
  DollarSign,
  Lightbulb,
  PieChart,
  Scale,
  TrendingUp,
  UserCheck,
  Users,
  Zap,
};

function getReportConfig(reportId: string | undefined): ReportConfig | undefined {
  return REPORTS.find((r) => r.id === reportId);
}

function buildExportPayload(
  reportId: string,
  title: string,
  filters: CommonReportFilters,
  data: unknown,
): { columns: ReportExportColumn[]; rows: Array<Record<string, unknown>> } | null {
  if (!data) return null;
  if (reportId === "project-health") {
    const rows = (data as { data: Array<Record<string, unknown>> }).data;
    return {
      columns: [
        { key: "code", header: "Code", width: 14 },
        { key: "name", header: "Project", width: 28 },
        { key: "companyName", header: "Company", width: 22 },
        { key: "status", header: "Status", width: 14 },
        { key: "progress", header: "Progress %", width: 12, type: "number" },
        { key: "taskCount", header: "Tasks", width: 10, type: "number" },
        { key: "overdueTasks", header: "Overdue", width: 10, type: "number" },
        { key: "milestoneCount", header: "Milestones", width: 12, type: "number" },
        { key: "budgetTotal", header: "Budget", width: 14, type: "currency" },
        { key: "budgetUtilization", header: "Budget %", width: 12, type: "number" },
      ],
      rows,
    };
  }
  if (reportId === "overdue-items") {
    const d = data as {
      tasks: Array<Record<string, unknown>>;
      actions: Array<Record<string, unknown>>;
      milestones: Array<Record<string, unknown>>;
    };
    const rows = [
      ...d.tasks.map((r) => ({ ...r, _kind: "Task" })),
      ...d.actions.map((r) => ({ ...r, _kind: "Action" })),
      ...d.milestones.map((r) => ({ ...r, _kind: "Milestone" })),
    ];
    return {
      columns: [
        { key: "_kind", header: "Type", width: 12 },
        { key: "code", header: "Code", width: 14 },
        { key: "title", header: "Title", width: 32 },
        { key: "projectName", header: "Project", width: 22 },
        { key: "priority", header: "Priority", width: 12 },
        { key: "daysOverdue", header: "Days Overdue", width: 14, type: "number" },
        { key: "assignedToName", header: "Assignee", width: 18 },
        { key: "ownerName", header: "Owner", width: 18 },
        { key: "dueDate", header: "Due Date", width: 14, type: "date" },
      ],
      rows,
    };
  }
  if (reportId === "project-timeline") {
    const rows = (data as { data: Array<Record<string, unknown>> }).data.map(
      (p) => ({
        name: p.name,
        startDate: p.startDate,
        endDate: p.endDate,
        status: p.status,
        progress: p.progress,
        phaseCount: Array.isArray(p.phases) ? p.phases.length : 0,
        milestoneCount: Array.isArray(p.milestones) ? p.milestones.length : 0,
      }),
    );
    return {
      columns: [
        { key: "name", header: "Project", width: 28 },
        { key: "status", header: "Status", width: 14 },
        { key: "progress", header: "Progress %", width: 12, type: "number" },
        { key: "startDate", header: "Start", width: 14, type: "date" },
        { key: "endDate", header: "End", width: 14, type: "date" },
        { key: "phaseCount", header: "Phases", width: 10, type: "number" },
        { key: "milestoneCount", header: "Milestones", width: 12, type: "number" },
      ],
      rows,
    };
  }
  if (reportId === "budget-vs-actual") {
    const rows = (data as { data: Array<Record<string, unknown>> }).data;
    return {
      columns: [
        { key: "budgetCode", header: "Code", width: 14 },
        { key: "budgetName", header: "Budget", width: 28 },
        { key: "projectName", header: "Project", width: 22 },
        { key: "totalAmount", header: "Allocated", width: 14, type: "currency" },
        { key: "approvedExpenses", header: "Spent", width: 14, type: "currency" },
        { key: "remainingAmount", header: "Remaining", width: 14, type: "currency" },
        { key: "utilizationPercent", header: "% Used", width: 12, type: "number" },
        { key: "status", header: "Status", width: 12 },
      ],
      rows,
    };
  }
  if (reportId === "expense-analysis") {
    const rows = (data as {
      byCategory: Array<Record<string, unknown>>;
    }).byCategory;
    return {
      columns: [
        { key: "category", header: "Category", width: 22 },
        { key: "subcategory", header: "Subcategory", width: 22 },
        { key: "count", header: "Count", width: 10, type: "number" },
        { key: "total", header: "Total", width: 14, type: "currency" },
        { key: "percent", header: "% of Total", width: 12, type: "number" },
      ],
      rows,
    };
  }
  if (reportId === "profitability") {
    const rows = (data as { data: Array<Record<string, unknown>> }).data;
    return {
      columns: [
        { key: "projectCode", header: "Code", width: 14 },
        { key: "projectName", header: "Project", width: 28 },
        { key: "companyName", header: "Company", width: 22 },
        { key: "estimatedCost", header: "Est. Cost", width: 14, type: "currency" },
        { key: "actualCost", header: "Actual Cost", width: 14, type: "currency" },
        { key: "quotedPrice", header: "Quoted", width: 14, type: "currency" },
        { key: "grossMargin", header: "Margin", width: 14, type: "currency" },
        { key: "marginPercent", header: "Margin %", width: 12, type: "number" },
      ],
      rows,
    };
  }
  if (reportId === "team-workload") {
    const rows = (data as { data: Array<Record<string, unknown>> }).data;
    return {
      columns: [
        { key: "userName", header: "Team Member", width: 22 },
        { key: "roleName", header: "Role", width: 18 },
        { key: "openTasks", header: "Open Tasks", width: 12, type: "number" },
        { key: "overdueTasks", header: "Overdue Tasks", width: 14, type: "number" },
        { key: "openActions", header: "Open Actions", width: 14, type: "number" },
        { key: "overdueActions", header: "Overdue Actions", width: 16, type: "number" },
        { key: "meetingsThisMonth", header: "Meetings", width: 12, type: "number" },
        { key: "workloadScore", header: "Workload", width: 12, type: "number" },
      ],
      rows,
    };
  }
  if (reportId === "activity-summary") {
    const rows = (data as { data: Array<Record<string, unknown>> }).data;
    return {
      columns: [
        { key: "userName", header: "Team Member", width: 22 },
        { key: "tasksCompleted", header: "Tasks", width: 10, type: "number" },
        { key: "actionsCompleted", header: "Actions", width: 10, type: "number" },
        { key: "meetingsAttended", header: "Meetings", width: 12, type: "number" },
        { key: "notesCreated", header: "Notes", width: 10, type: "number" },
        { key: "ideasSubmitted", header: "Ideas", width: 10, type: "number" },
        { key: "decisionsParticipated", header: "Decisions", width: 12, type: "number" },
      ],
      rows,
    };
  }
  if (reportId === "meeting-attendance") {
    const rows = (data as { data: Array<Record<string, unknown>> }).data;
    return {
      columns: [
        { key: "meetingTitle", header: "Meeting", width: 30 },
        { key: "meetingDate", header: "Date", width: 14, type: "date" },
        { key: "type", header: "Type", width: 12 },
        { key: "status", header: "Status", width: 12 },
        { key: "organizerName", header: "Organizer", width: 18 },
        { key: "totalInvited", header: "Invited", width: 10, type: "number" },
        { key: "totalAttended", header: "Attended", width: 10, type: "number" },
        { key: "attendanceRate", header: "Rate %", width: 10, type: "number" },
      ],
      rows,
    };
  }
  if (reportId === "tasks-status") {
    const rows = (data as { byProject: Array<Record<string, unknown>> }).byProject;
    return {
      columns: [
        { key: "projectName", header: "Project", width: 28 },
        { key: "total", header: "Total", width: 10, type: "number" },
        { key: "completed", header: "Completed", width: 12, type: "number" },
        { key: "open", header: "Open", width: 10, type: "number" },
        { key: "overdue", header: "Overdue", width: 10, type: "number" },
      ],
      rows,
    };
  }
  if (reportId === "actions-tracker") {
    const rows = (data as {
      overdueByAssignee: Array<Record<string, unknown>>;
    }).overdueByAssignee;
    return {
      columns: [
        { key: "name", header: "Assignee", width: 22 },
        { key: "overdueCount", header: "Overdue Count", width: 14, type: "number" },
      ],
      rows,
    };
  }
  if (reportId === "decisions-log") {
    const rows = (data as { data: Array<Record<string, unknown>> }).data;
    return {
      columns: [
        { key: "code", header: "Code", width: 14 },
        { key: "title", header: "Title", width: 30 },
        { key: "category", header: "Category", width: 14 },
        { key: "status", header: "Status", width: 14 },
        { key: "submittedByName", header: "Submitter", width: 18 },
        { key: "decidedByName", header: "Decided By", width: 18 },
        { key: "submittedAt", header: "Submitted", width: 14, type: "date" },
        { key: "decidedAt", header: "Decided", width: 14, type: "date" },
        { key: "daysToDecision", header: "Days", width: 10, type: "number" },
        { key: "projectName", header: "Project", width: 22 },
      ],
      rows,
    };
  }
  if (reportId === "ideas-pipeline") {
    const rows = (data as { topIdeas: Array<Record<string, unknown>> }).topIdeas;
    return {
      columns: [
        { key: "code", header: "Code", width: 14 },
        { key: "title", header: "Title", width: 30 },
        { key: "status", header: "Status", width: 14 },
        { key: "votesUp", header: "Up", width: 8, type: "number" },
        { key: "votesDown", header: "Down", width: 8, type: "number" },
        { key: "netVotes", header: "Net", width: 8, type: "number" },
      ],
      rows,
    };
  }

  void title;
  void filters;
  return null;
}

function filtersToString(filters: CommonReportFilters): string {
  const out: string[] = [];
  if (filters.dateFrom) out.push(`from ${filters.dateFrom}`);
  if (filters.dateTo) out.push(`to ${filters.dateTo}`);
  if (filters.companyId) out.push(`company=${filters.companyId}`);
  if (filters.projectId) out.push(`project=${filters.projectId}`);
  if (filters.status) out.push(`status=${filters.status}`);
  return out.join(", ");
}

export default function ReportViewPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const config = getReportConfig(reportId);

  const [filters, setFilters] = useState<CommonReportFilters>(defaultFilters());
  const [enabled, setEnabled] = useState(false);
  const [exportLoading, setExportLoading] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Run all hooks unconditionally; only the matching one is enabled
  const phHook = useProjectHealthReport(filters, enabled && reportId === "project-health");
  const oiHook = useOverdueItemsReport(filters, enabled && reportId === "overdue-items");
  const ptHook = useProjectTimelineReport(filters, enabled && reportId === "project-timeline");
  const bvaHook = useBudgetVsActualReport(filters, enabled && reportId === "budget-vs-actual");
  const eaHook = useExpenseAnalysisReport(filters, enabled && reportId === "expense-analysis");
  const profHook = useProfitabilityReport(filters, enabled && reportId === "profitability");
  const twHook = useTeamWorkloadReport(filters, enabled && reportId === "team-workload");
  const asHook = useActivitySummaryReport(filters, enabled && reportId === "activity-summary");
  const maHook = useMeetingAttendanceReport(filters, enabled && reportId === "meeting-attendance");
  const tsHook = useTasksStatusReport(filters, enabled && reportId === "tasks-status");
  const atHook = useActionsTrackerReport(filters, enabled && reportId === "actions-tracker");
  const dlHook = useDecisionsLogReport(filters, enabled && reportId === "decisions-log");
  const ipHook = useIdeasPipelineReport(filters, enabled && reportId === "ideas-pipeline");

  const activeHook = useMemo(() => {
    switch (reportId) {
      case "project-health": return phHook;
      case "overdue-items": return oiHook;
      case "project-timeline": return ptHook;
      case "budget-vs-actual": return bvaHook;
      case "expense-analysis": return eaHook;
      case "profitability": return profHook;
      case "team-workload": return twHook;
      case "activity-summary": return asHook;
      case "meeting-attendance": return maHook;
      case "tasks-status": return tsHook;
      case "actions-tracker": return atHook;
      case "decisions-log": return dlHook;
      case "ideas-pipeline": return ipHook;
      default: return null;
    }
  }, [
    reportId,
    phHook,
    oiHook,
    ptHook,
    bvaHook,
    eaHook,
    profHook,
    twHook,
    asHook,
    maHook,
    tsHook,
    atHook,
    dlHook,
    ipHook,
  ]);

  // Auto-run on first mount so users see something useful right away
  useEffect(() => {
    if (!enabled) setEnabled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!config) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Reports", href: "/reports" },
          { label: "Not found" },
        ]}
      >
        <div className="rounded-xl border bg-white p-8 text-center text-sm text-muted-foreground"
          style={{ borderColor: "hsl(210,30%,85%)" }}>
          Report not found.
        </div>
      </AppLayout>
    );
  }

  const reportConfig = config;
  const Icon = ICON_MAP[reportConfig.icon] ?? Activity;
  const color = COLOR_PALETTE[reportConfig.color];

  function handleRun() {
    setEnabled(true);
    if (activeHook) activeHook.refetch();
  }

  function handleReset() {
    setFilters(defaultFilters());
  }

  async function handleExport(format: "csv" | "excel" | "pdf") {
    if (!activeHook?.data || !config) return;
    const built = buildExportPayload(
      reportConfig.id,
      reportConfig.title,
      filters,
      activeHook.data,
    );
    if (!built) {
      setExportError("Export not available for this report yet.");
      setTimeout(() => setExportError(null), 3000);
      return;
    }
    setExportLoading(format);
    try {
      await exportReport(
        {
          reportId: reportConfig.id,
          title: reportConfig.title,
          columns: built.columns,
          data: built.rows,
          filters: filtersToString(filters),
          format,
          orientation: "landscape",
        },
        accessToken,
      );
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Export failed");
      setTimeout(() => setExportError(null), 3000);
    } finally {
      setExportLoading(null);
    }
  }

  const isLoading = activeHook?.isFetching ?? false;
  const isError = activeHook?.isError ?? false;
  const error = activeHook?.error as Error | null | undefined;
  const data = activeHook?.data;

  function renderContent() {
    if (!enabled) {
      return (
        <div className="rounded-xl border bg-white py-16 text-center text-sm text-muted-foreground"
          style={{ borderColor: "hsl(210,30%,85%)" }}>
          Adjust filters and click Run Report to load data.
        </div>
      );
    }
    if (isLoading && !data) {
      return (
        <div className="rounded-xl border bg-white py-16 text-center text-sm text-muted-foreground"
          style={{ borderColor: "hsl(210,30%,85%)" }}>
          <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" strokeWidth={1.5} />
          Generating report…
        </div>
      );
    }
    if (isError) {
      return (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
          <div className="font-medium">Failed to run report</div>
          <div className="mt-1 font-mono text-xs opacity-80">
            {error?.message ?? "Unknown error"}
          </div>
        </div>
      );
    }
    if (!data) {
      return null;
    }

    switch (reportConfig.id) {
      case "project-health":
        return (
          <ProjectHealthReport
            data={(data as { data: unknown[] }).data as never[]}
          />
        );
      case "overdue-items":
        return <OverdueItemsReport data={data as never} />;
      case "project-timeline":
        return (
          <ProjectTimelineReport
            data={(data as { data: unknown[] }).data as never[]}
          />
        );
      case "budget-vs-actual":
        return <BudgetVsActualReport data={data as never} />;
      case "expense-analysis":
        return <ExpenseAnalysisReport data={data as never} />;
      case "profitability":
        return <ProfitabilityReport data={data as never} />;
      case "team-workload":
        return (
          <TeamWorkloadReport
            data={(data as { data: unknown[] }).data as never[]}
          />
        );
      case "activity-summary":
        return (
          <ActivitySummaryReport
            data={(data as { data: unknown[] }).data as never[]}
          />
        );
      case "meeting-attendance":
        return <MeetingAttendanceReport data={data as never} />;
      case "tasks-status":
        return <TasksStatusReport data={data as never} />;
      case "actions-tracker":
        return <ActionsTrackerReport data={data as never} />;
      case "decisions-log":
        return <DecisionsLogReport data={data as never} />;
      case "ideas-pipeline":
        return <IdeasPipelineReport data={data as never} />;
      default:
        return (
          <div className="text-sm text-muted-foreground">
            Report renderer not implemented.
          </div>
        );
    }
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Reports", href: "/reports" },
        { label: reportConfig.title },
      ]}
    >
      <div className="w-full space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/reports")}
              className="flex h-8 w-8 items-center justify-center rounded-md border bg-white transition-colors hover:bg-[hsl(210,50%,96%)]"
              style={{ borderColor: "hsl(210,30%,85%)" }}
              title="Back to Reports"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: color.bg }}
            >
              <Icon className="h-5 w-5" strokeWidth={1.6} style={{ color: color.fg }} />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {reportConfig.title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data || exportLoading !== null}
              onClick={() => handleExport("csv")}
            >
              {exportLoading === "csv" ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : null}
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!data || exportLoading !== null}
              onClick={() => handleExport("excel")}
            >
              {exportLoading === "excel" ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : null}
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!data || exportLoading !== null}
              onClick={() => handleExport("pdf")}
            >
              {exportLoading === "pdf" ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : null}
              PDF
            </Button>
          </div>
        </div>

        {exportError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {exportError}
          </div>
        )}

        <ReportFilters
          category={reportConfig.category}
          filters={filters}
          onChange={setFilters}
          onRun={handleRun}
          onReset={handleReset}
          loading={isLoading}
        />

        {renderContent()}
      </div>
    </AppLayout>
  );
}
